<template>
  <a-form-model ref="innerForm" :model="innerModel">
    <div class="biz-row">
      <label class="biz-row-label"><span class="biz-row-req">*</span>服务领域</label>
      <div class="biz-row-control">
        <a-form-model-item prop="domainId" :rules="domainRules" :wrapper-col="{ span: 24 }">
          <a-select
            v-model="innerModel.domainId"
            placeholder="请选择服务领域"
            :loading="loading"
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            @change="handleDomainChange"
          >
            <a-select-option v-for="item in domains" :key="item.id" :value="String(item.id)">
              {{ item.name }}
            </a-select-option>
            <a-select-option value="__new_domain__">
              <span class="biz-add-option-text">＋ 新建服务领域…</span>
            </a-select-option>
          </a-select>
        </a-form-model-item>
      </div>
    </div>
    <div class="biz-row">
      <label class="biz-row-label"><span class="biz-row-req">*</span>业务类型</label>
      <div class="biz-row-control">
        <a-form-model-item prop="typeCode" :rules="typeRules" :wrapper-col="{ span: 24 }">
          <a-select
            v-model="innerModel.typeCode"
            :placeholder="typePlaceholder"
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            @change="handleTypeChange"
          >
            <a-select-option v-for="item in typeOptions" :key="item.code" :value="item.code">
              {{ item.unregistered ? `${item.name}（未登记）` : `${item.name}（${item.code}）` }}
            </a-select-option>
            <a-select-option value="__create__">
              <span class="biz-add-option-text">＋ 新建业务类型…</span>
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <!-- 后端无单独创建领域的接口：新建领域需连同首个业务类型一起提交 -->
        <div v-if="domainPanelVisible" class="biz-create-panel">
          <div class="panel-head">
            <span class="panel-title">新建服务领域</span>
            <a-button type="link" size="small" @click="cancelDomainCreate">返回选择</a-button>
          </div>
          <div class="panel-row">
            <a-input
              v-model.trim="domainPanel.domainName"
              :maxLength="32"
              placeholder="服务领域名称（同名直接复用）"
            />
            <a-input
              v-model.trim="domainPanel.typeName"
              :maxLength="32"
              placeholder="该领域下首个业务类型名称"
            />
            <a-button
              type="primary"
              :loading="domainPanel.loading"
              @click="submitDomainCreate"
            >创建</a-button>
          </div>
          <div class="panel-hint">编码由系统自动生成，无需填写</div>
        </div>

        <div v-if="typePanelVisible" class="biz-create-panel">
          <div class="panel-head">
            <span class="panel-title">新建业务类型</span>
            <a-button type="link" size="small" @click="cancelTypeCreate">返回选择</a-button>
          </div>
          <div class="panel-row">
            <a-input
              v-model.trim="typePanel.typeName"
              :maxLength="32"
              placeholder="业务类型名称（归属左侧所选服务领域）"
            />
            <a-button
              type="primary"
              :loading="typePanel.loading"
              @click="submitTypeCreate"
            >创建</a-button>
          </div>
          <div class="panel-hint">编码由系统自动生成，无需填写</div>
        </div>
      </div>
    </div>
  </a-form-model>
</template>

<script>
/**
 * BizTypeSelect 业务类型两段式选择器（服务领域 + 业务类型）
 *
 * v-model 绑定业务类型的完整编码（refCode）；字典自行加载，两个下拉都带
 * 「＋ 新建…」内联创建入口（领域创建需连同首个业务类型一起提交）。
 * 存量编码不在字典时以「未登记」选项回显，不丢值。
 *
 * 校验：内部为独立的 a-form-model，错误文案与红框由 antd 原生渲染在
 * 各自行的下拉下方；父级提交时调用 validate()（返回 Promise<boolean>）。
 */
import { getBizTypeList, createBizType } from "@/api/intelligentCustomerService";

// 两段式下拉的“新建”哨兵值：选中即展开内联创建面板，不作为真实表单值提交
const NEW_DOMAIN_VALUE = "__new_domain__";
const NEW_TYPE_VALUE = "__create__";

const getDefaultDomainPanel = () => ({ domainName: "", typeName: "", loading: false });
const getDefaultTypePanel = () => ({ typeName: "", loading: false });

// 业务类型条目兼容平铺返回结构（categoryLevel/parentId 驼峰或下划线）
const normalizeBizType = (item) => {
  const parentId = item.parentId ?? item.parent_id ?? null;
  const categoryLevel = Number(
    item.categoryLevel ?? item.category_level ?? (parentId != null ? 2 : 1)
  );
  return {
    id: item.id,
    code: item.code || "",
    name: item.name || "",
    categoryLevel,
    parentId,
  };
};

export default {
  name: "BizTypeSelect",
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      innerModel: {
        domainId: undefined,
        typeCode: undefined,
      },
      list: [],
      loading: false,
      domainPanelVisible: false,
      typePanelVisible: false,
      domainPanel: getDefaultDomainPanel(),
      typePanel: getDefaultTypePanel(),
      // 内部修改 v-model 时跳过 value 回同步，避免覆盖用户刚做的选择
      suppressWatch: false,
      domainRules: [
        {
          validator: (rule, value, callback) => {
            if (!value || value === NEW_DOMAIN_VALUE) {
              return callback(new Error("请选择服务领域"));
            }
            callback();
          },
          trigger: "change",
        },
      ],
      typeRules: [
        {
          validator: (rule, value, callback) => {
            if (!value || value === NEW_TYPE_VALUE) {
              return callback(new Error("请选择业务类型"));
            }
            callback();
          },
          trigger: "change",
        },
      ],
    };
  },
  computed: {
    domains() {
      return this.list.filter((item) => item.categoryLevel === 1);
    },
    typePlaceholder() {
      if (!this.innerModel.domainId) return "请先选择服务领域";
      return this.innerModel.domainId === NEW_DOMAIN_VALUE ? "请先完成领域创建" : "请选择业务类型";
    },
    currentDomainTypes() {
      const domainId = this.innerModel.domainId;
      if (!domainId || domainId === NEW_DOMAIN_VALUE) return [];
      return this.list.filter(
        (item) => item.categoryLevel === 2 && String(item.parentId) === String(domainId)
      );
    },
    typeOptions() {
      const options = this.currentDomainTypes.map((item) => ({
        code: item.code,
        name: item.name,
      }));
      // 存量编码不在字典中时以「未登记」选项回显，避免编辑丢值
      const typeCode = this.innerModel.typeCode;
      if (
        typeCode &&
        typeCode !== NEW_TYPE_VALUE &&
        !options.some((item) => item.code === typeCode)
      ) {
        options.unshift({ code: typeCode, name: typeCode, unregistered: true });
      }
      return options;
    },
  },
  watch: {
    value(val) {
      if (this.suppressWatch) {
        this.suppressWatch = false;
        return;
      }
      this.syncFromValue(val);
    },
  },
  mounted() {
    this.fetchList();
    this.syncFromValue(this.value);
  },
  methods: {
    isSuccess(res) {
      return res && [0, "0", 200, "200"].includes(res.code);
    },
    // 供父级提交时调用（await）：错误文案与红框由内部表单项原生渲染
    validate() {
      return new Promise((resolve) => {
        this.$refs.innerForm.validate((valid) => resolve(!!valid));
      });
    },
    // 供父级在弹窗重置时显式调用：value 未变化（如 undefined→undefined）时
    // watch 不会触发，靠它可以确定性地复位内部选择与校验状态
    reset() {
      this.syncFromValue(undefined);
    },
    // 外部值变化（编辑回显/表单重置）时同步内部两个下拉
    syncFromValue(val) {
      if (!val) {
        this.innerModel.domainId = undefined;
        this.innerModel.typeCode = undefined;
        this.domainPanelVisible = false;
        this.typePanelVisible = false;
        this.domainPanel = getDefaultDomainPanel();
        this.typePanel = getDefaultTypePanel();
        this.$nextTick(() => {
          if (this.$refs.innerForm) {
            this.$refs.innerForm.clearValidate();
          }
        });
        return;
      }
      // 字典可能晚于外部值到达（异步加载），每次都重新映射，
      // 保证回显时服务领域能按编码反查出所属领域
      const matched = this.list.find(
        (item) => item.categoryLevel === 2 && item.code === val
      );
      this.innerModel.typeCode = val;
      if (matched) {
        this.innerModel.domainId = String(matched.parentId);
      }
    },
    emitValue(code) {
      // 仅在值真正变化时标记跳过回同步：value 不变时 watch 不会触发，
      // 提前置位会导致标记泄漏、吞掉后续外部赋值（编辑回显偶发失效）
      this.suppressWatch = code !== this.value;
      this.$emit("input", code);
      this.$emit("change", code);
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await getBizTypeList();
        if (this.isSuccess(res)) {
          const raw = Array.isArray(res.data) ? res.data : res.data?.list || [];
          this.list = raw.map(normalizeBizType);
          // 字典就绪后重新映射外部值（可能在加载前已回显）
          this.syncFromValue(this.value);
        }
      } catch {
        // 字典加载失败不阻塞表单，仍可通过内联新建补建业务类型
      } finally {
        this.loading = false;
      }
    },
    handleDomainChange(value) {
      this.innerModel.typeCode = undefined;
      this.typePanelVisible = false;
      this.typePanel = getDefaultTypePanel();
      if (value === NEW_DOMAIN_VALUE) {
        this.domainPanelVisible = true;
      } else {
        this.domainPanelVisible = false;
        this.domainPanel = getDefaultDomainPanel();
      }
      this.emitValue(undefined);
    },
    handleTypeChange(value) {
      if (value === NEW_TYPE_VALUE) {
        const domainOk =
          this.innerModel.domainId &&
          this.innerModel.domainId !== NEW_DOMAIN_VALUE &&
          this.domains.some(
            (item) => String(item.id) === String(this.innerModel.domainId)
          );
        if (!domainOk) {
          this.$message.warning("请先选择服务领域");
          this.innerModel.typeCode = undefined;
          return;
        }
        this.typePanelVisible = true;
        this.emitValue(undefined);
        return;
      }
      this.typePanelVisible = false;
      this.typePanel = getDefaultTypePanel();
      this.emitValue(value);
    },
    cancelDomainCreate() {
      this.innerModel.domainId = undefined;
      this.domainPanelVisible = false;
      this.domainPanel = getDefaultDomainPanel();
    },
    cancelTypeCreate() {
      this.innerModel.typeCode = undefined;
      this.typePanelVisible = false;
      this.typePanel = getDefaultTypePanel();
    },
    findTypeUnderDomain(domainId, name) {
      return this.list.find(
        (item) =>
          item.categoryLevel === 2 &&
          String(item.parentId) === String(domainId) &&
          item.name === name
      );
    },
    // 创建成功后回填选中项：优先按 领域名+类型名 在重载后的字典中匹配
    applyCreatedBizType(domainName, typeName) {
      const domain = this.domains.find((item) => item.name === domainName);
      const type = domain ? this.findTypeUnderDomain(domain.id, typeName) : null;
      if (domain && type) {
        this.innerModel.domainId = String(domain.id);
        this.innerModel.typeCode = type.code;
        this.emitValue(type.code);
        return true;
      }
      if (domain) {
        this.innerModel.domainId = String(domain.id);
        this.innerModel.typeCode = undefined;
        this.emitValue(undefined);
      }
      return false;
    },
    async submitDomainCreate() {
      const domainName = this.domainPanel.domainName.trim();
      const typeName = this.domainPanel.typeName.trim();
      if (!domainName) {
        this.$message.error("请输入服务领域名称");
        return;
      }
      if (!typeName) {
        this.$message.error("请输入业务类型名称");
        return;
      }
      const existDomain = this.domains.find((item) => item.name === domainName);
      if (existDomain && this.findTypeUnderDomain(existDomain.id, typeName)) {
        this.$message.error("该服务领域下已存在同名业务类型");
        return;
      }
      this.domainPanel.loading = true;
      try {
        const res = await createBizType({ domainName, name: typeName });
        if (!this.isSuccess(res)) {
          this.$message.error(res?.msg || "创建失败");
          return;
        }
        this.$message.success("业务类型已创建");
        await this.fetchList();
        if (!this.applyCreatedBizType(domainName, typeName)) {
          this.$message.warning("已创建成功，请手动选择业务类型");
        }
        this.domainPanelVisible = false;
        this.domainPanel = getDefaultDomainPanel();
      } catch {
        this.$message.error("创建失败");
      } finally {
        this.domainPanel.loading = false;
      }
    },
    async submitTypeCreate() {
      const typeName = this.typePanel.typeName.trim();
      const domain = this.domains.find(
        (item) => String(item.id) === String(this.innerModel.domainId)
      );
      if (!typeName) {
        this.$message.error("请输入业务类型名称");
        return;
      }
      if (!domain) {
        this.$message.error("请先选择服务领域");
        return;
      }
      if (this.findTypeUnderDomain(domain.id, typeName)) {
        this.$message.error("该服务领域下已存在同名业务类型");
        return;
      }
      this.typePanel.loading = true;
      try {
        const res = await createBizType({ domainName: domain.name, name: typeName });
        if (!this.isSuccess(res)) {
          this.$message.error(res?.msg || "创建失败");
          return;
        }
        this.$message.success("业务类型已创建");
        await this.fetchList();
        if (!this.applyCreatedBizType(domain.name, typeName)) {
          this.$message.warning("已创建成功，请手动选择业务类型");
        }
        this.typePanelVisible = false;
        this.typePanel = getDefaultTypePanel();
      } catch {
        this.$message.error("创建失败");
      } finally {
        this.typePanel.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.biz-row {
  display: flex;

  & + .biz-row {
    margin-top: 2px;
  }

  // label 列按表单栅格比例（6/22）定位，与弹窗其他字段的 label 竖线对齐；
  // 颜色/星号位置/冒号均对齐 antd 表单 label 的默认样式
  .biz-row-label {
    width: 27.27%;
    flex-shrink: 0;
    padding-right: 12px;
    text-align: right;
    font-size: 14px;
    line-height: 32px;
    color: rgba(0, 0, 0, 0.85);

    &::after {
      content: "：";
    }

    .biz-row-req {
      color: #f5222d;
      margin-right: 4px;
    }
  }

  .biz-row-control {
    flex: 1;
    min-width: 0;
  }
}

// 嵌套表单项沿用 antd 校验样式（错误文案/红框由 antd 渲染），
// 仅收紧默认 24px 的纵向间距
::v-deep .ant-form-item {
  margin-bottom: 8px;
}

// 下拉里的「＋ 新建…」入口用主题蓝突出操作属性（下拉经 getPopupContainer
// 渲染在组件内部，scoped 样式可及；普通选项不受影响）
.biz-add-option-text {
  color: #1890ff;
}

.biz-create-panel {
  margin-top: 8px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;

  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .panel-title {
      font-size: 13px;
      font-weight: 600;
    }
  }

  .panel-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .ant-input {
      flex: 1;
    }
  }

  .panel-hint {
    margin-top: 6px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}
</style>
